import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import FecthingSpinner from "../../Shared/FecthingSpinner";
import httpService from "../../../services/http.service";
import MyOpinionLogo from "../../../assets/images/MyOpinion_Logo_Head.png";

const SUPPORT_LABELS = {
  payin: "Encaissement",
  payout: "Décaissement",
  both: "Encaissement & décaissement",
};

const PLATFORM_LABELS = {
  all: "Toutes plateformes",
  web: "Web",
  android: "Android",
  ios: "iOS",
};

function labelSupport(value) {
  if (!value) return "—";
  return SUPPORT_LABELS[value] || value;
}

function labelPlatform(value) {
  if (!value) return "—";
  return PLATFORM_LABELS[value] || value;
}

/**
 * Liste des méthodes de paiement disponibles pour un pays (pivot + méthode active).
 * Données : GET /payment-countries/:id
 */
const PaymentCountryMethods = ({ match }) => {
  const { id } = match.params;

  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState(null);
  const [methods, setMethods] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await httpService.get(`/payment-countries/${id}`);
        const payload = response?.data?.data || {};

        setCountry(payload.country || null);
        setMethods(
          Array.isArray(payload.payment_methods) ? payload.payment_methods : []
        );
      } catch (error) {
        console.error(
          "Erreur chargement méthodes du pays :",
          error?.response || error
        );
        alert("Impossible de charger les méthodes de paiement pour ce pays.");
        setCountry(null);
        setMethods([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="container-fluid payment-country-methods-page">
        <AppBreadcrumb title="Gestion des méthodes" />
        <FecthingSpinner />
      </div>
    );
  }

  if (!country) {
    return (
      <div className="container-fluid payment-country-methods-page">
        <AppBreadcrumb title="Gestion des méthodes" />
        <div className="card border-0 shadow-sm mt-2">
          <div className="card-body text-center py-5">
            <span
              className="icon-globe text-muted d-block mb-3"
              style={{ fontSize: "2.5rem" }}
              aria-hidden
            />
            <h5 className="text-secondary">Pays introuvable</h5>
            <p className="text-muted small mb-4">
              Ce pays de paiement n’existe pas ou n’est plus actif.
            </p>
            <Link
              to="/payment-countries"
              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
            >
              <i className="icon-arrow-left mr-2" aria-hidden />
              Retour à la liste des pays
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const title = `Gestion des méthodes — ${country.name_fr || country.code}`;

  return (
    <div className="container-fluid payment-country-methods-page">
      <AppBreadcrumb title={title}>
        <div className="col-md-6 col-sm-12 text-md-right">
          <nav className="d-inline-block text-left text-md-right small text-muted mb-0">
            <Link to="/payment-countries" className="text-muted">
              Pays de paiement
            </Link>
            <span className="mx-1">/</span>
            <span className="text-muted">{country.code}</span>
            <span className="mx-1">/</span>
            <span className="text-dark font-weight-medium">
              Gestion des méthodes
            </span>
          </nav>
        </div>
      </AppBreadcrumb>

      {/* En-tête pays */}
      <div
        className="card border-0 shadow-sm mb-4 overflow-hidden"
        style={{ borderLeft: "4px solid #28a745" }}
      >
        <div className="card-body p-4">
          <div className="row align-items-center">
            <div className="col-auto pr-3">
              <div
                className="rounded-lg bg-light d-flex align-items-center justify-content-center shadow-sm"
                style={{
                  width: 72,
                  height: 72,
                  overflow: "hidden",
                }}
              >
                {country.flag ? (
                  <img
                    src={country.flag}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span className="h5 mb-0 text-muted font-weight-bold">
                    {country.code}
                  </span>
                )}
              </div>
            </div>
            <div className="col pl-0 pl-md-2">
              <p className="text-uppercase text-muted small mb-1 letter-spacing">
                Méthodes actives pour ce marché
              </p>
              <h4 className="mb-2 font-weight-bold text-dark">
                {country.name_fr}
                <span className="text-muted font-weight-normal h5 mb-0">
                  {" "}
                  ({country.name_en})
                </span>
              </h4>
              <div className="d-flex flex-wrap align-items-center">
                <span className="badge badge-light text-dark border mr-2 mb-1">
                  {country.code}
                </span>
                <span className="badge badge-light text-dark border mr-2 mb-1">
                  {country.currency_code}
                </span>
                {country.dial_code ? (
                  <span className="badge badge-light text-dark border mb-1">
                    +{String(country.dial_code).replace(/^\+/, "")}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="col-12 col-lg-auto mt-4 mt-lg-0 text-lg-right">
              <Link
                to={`/payment-countries/${id}/methods/activation`}
                className="btn btn-success btn-sm d-inline-flex align-items-center justify-content-center px-3"
              >
                <i className="icon-check mr-2 flex-shrink-0" aria-hidden />
                Gestion des méthodes
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Synthèse — cartes indicateurs */}
      <div className="row mb-3 payment-country-stat-cards">
        <div className="col-sm-4 mb-2 mb-sm-0">
          <div className="payment-country-stat-card payment-country-stat-card--methods h-100 shadow-sm d-flex flex-column px-3 pt-3 pb-2">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <span className="payment-country-stat-card__label">
                Méthodes listées
              </span>
              <span
                className="payment-country-stat-card__icon payment-country-stat-card__icon--success"
                aria-hidden
              >
                <i className="icon-credit-card" />
              </span>
            </div>
            <div className="payment-country-stat-card__value text-success">
              {methods.length}
            </div>
            <p className="payment-country-stat-card__hint text-muted mb-0">
              Méthodes visibles sur cette page pour le pays sélectionné
            </p>
          </div>
        </div>
        <div className="col-sm-4 mb-2 mb-sm-0">
          <div className="payment-country-stat-card payment-country-stat-card--currency h-100 shadow-sm d-flex flex-column px-3 pt-3 pb-2">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <span className="payment-country-stat-card__label">
                Monnaie locale
              </span>
              <span
                className="payment-country-stat-card__icon payment-country-stat-card__icon--info"
                aria-hidden
              >
                <i className="icon-wallet" />
              </span>
            </div>
            <div className="payment-country-stat-card__value payment-country-stat-card__value--text text-dark">
              <span className="d-block text-truncate" title={country.currency_name}>
                {country.currency_name || "—"}
              </span>
              <span className="payment-country-stat-card__code">
                {country.currency_code}
              </span>
            </div>
            <p className="payment-country-stat-card__hint mb-0">
              Devise utilisée pour les paiements sur ce marché
            </p>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="payment-country-stat-card payment-country-stat-card--rule h-100 shadow-sm d-flex flex-column px-3 pt-3 pb-2">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <span className="payment-country-stat-card__label">
                Règle d’affichage
              </span>
              <span
                className="payment-country-stat-card__icon payment-country-stat-card__icon--neutral"
                aria-hidden
              >
                <i className="icon-info" />
              </span>
            </div>
            <ul className="payment-country-stat-card__rules list-unstyled mb-0 pl-0 small text-dark">
              <li className="payment-country-stat-card__rule-item d-flex align-items-start">
                <span className="payment-country-stat-card__bullet bg-success" />
                <span>
                  Lien pays ↔ méthode actif dans la configuration
                </span>
              </li>
              <li className="payment-country-stat-card__rule-item d-flex align-items-start">
                <span className="payment-country-stat-card__bullet bg-success" />
                <span>
                  Méthode <strong>available</strong> et <strong>active</strong>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Liste — tableau style clair (sans table-custom / fond sombre) */}
      <div className="card border-0 shadow-sm payment-country-methods-catalog-card overflow-hidden">
        <div className="card-header payment-country-methods-catalog-header border-0 py-3 d-flex flex-wrap align-items-center justify-content-between">
          <div className="d-flex align-items-start">
            <span
              className="payment-country-methods-catalog-header__accent d-none d-sm-flex flex-shrink-0 mr-3"
              aria-hidden
            />
            <div>
              <h6 className="mb-1 font-weight-bold text-dark">
                Catalogue pour ce pays
              </h6>
              <small className="text-muted d-block mb-0 payment-country-methods-catalog-header__subtitle">
                Méthodes effectivement proposées aux utilisateurs de ce pays
              </small>
            </div>
          </div>
          {methods.length > 0 ? (
            <span className="badge badge-success badge-pill px-3 py-2 mt-2 mt-md-0 payment-country-methods-catalog-count">
              {methods.length} méthode{methods.length > 1 ? "s" : ""}
            </span>
          ) : null}
        </div>

        {methods.length === 0 ? (
          <div className="card-body text-center py-5 px-4">
            <div
              className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: 80, height: 80 }}
            >
              <i
                className="icon-credit-card text-muted"
                style={{ fontSize: "2rem" }}
                aria-hidden
              />
            </div>
            <h5 className="text-secondary mb-2">Aucune méthode pour l’instant</h5>
            <p className="text-muted small mb-4 mx-auto" style={{ maxWidth: 420 }}>
              Activez des méthodes et définissez les frais dans la configuration
              par pays pour qu’elles apparaissent ici.
            </p>
            <Link
              to={`/payment-countries/${id}/methods/activation`}
              className="btn btn-success btn-sm d-inline-flex align-items-center px-4"
            >
              <i className="icon-check mr-2" aria-hidden />
              Gestion des méthodes
            </Link>
          </div>
        ) : (
          <div className="table-responsive payment-country-methods-catalog-wrap px-0">
            <table className="table table-hover payment-country-methods-catalog-table mb-0">
              <thead>
                <tr>
                  <th scope="col">Méthode</th>
                  <th scope="col">Référence</th>
                  <th scope="col">Support</th>
                  <th scope="col">Plateforme</th>
                  <th scope="col">État</th>
                  <th scope="col" className="text-right">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {methods.map((method) => (
                  <tr key={method.method_id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <img
                          src={method.method_logo || MyOpinionLogo}
                          alt=""
                          className="flex-shrink-0 mr-2 payment-country-methods-catalog-avatar"
                          width={36}
                          height={36}
                        />
                        <div>
                          <div className="payment-country-methods-catalog-name mb-0">
                            {method.method_name}
                          </div>
                          <span className="payment-country-methods-catalog-meta">
                            ID {method.method_id}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <code className="payment-country-methods-catalog-tag">
                        {method.method_tag || "—"}
                      </code>
                    </td>
                    <td>
                      <span className="payment-country-methods-catalog-pill">
                        {labelSupport(method.method_support)}
                      </span>
                    </td>
                    <td>
                      <span className="payment-country-methods-catalog-pill">
                        {labelPlatform(method.method_platform)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`payment-country-methods-catalog-status ${
                          method.method_state === "available"
                            ? "is-available"
                            : "is-other"
                        }`}
                      >
                        {method.method_state === "available"
                          ? "Disponible"
                          : method.method_state || "—"}
                      </span>
                    </td>
                    <td className="text-right">
                      <Link
                        to={`/payment-countries/${id}/methods/${method.method_id}`}
                        className="btn btn-sm btn-outline-primary payment-country-methods-catalog-btn"
                      >
                        Détails
                        <i className="icon-arrow-right ml-1" aria-hidden />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 mb-2">
        <Link
          to="/payment-countries"
          className="btn btn-link text-muted pl-0 d-inline-flex align-items-center"
        >
          <i className="icon-arrow-left mr-2" aria-hidden />
          Liste des pays de paiement
        </Link>
      </div>

      <style>{`
        .payment-country-methods-page .letter-spacing { letter-spacing: 0.06em; }

        /* ——— Catalogue pour ce pays : tableau clair ——— */
        .payment-country-methods-page .payment-country-methods-catalog-card {
          border-radius: 8px;
        }
        .payment-country-methods-page .payment-country-methods-catalog-header {
          background: linear-gradient(180deg, #f8f9fb 0%, #fff 100%);
        }
        .payment-country-methods-page .payment-country-methods-catalog-header__accent {
          width: 4px;
          min-height: 2.5rem;
          border-radius: 4px;
          background: linear-gradient(180deg, #28a745, #20c997);
          margin-top: 2px;
        }
        .payment-country-methods-page .payment-country-methods-catalog-header__subtitle {
          font-size: 0.8125rem;
          line-height: 1.4;
          max-width: 36rem;
        }
        .payment-country-methods-page .payment-country-methods-catalog-count {
          font-weight: 600;
          font-size: 0.8rem;
        }
        .payment-country-methods-page .payment-country-methods-catalog-wrap {
          border-top: 1px solid #e9ecef;
        }
        .payment-country-methods-page .payment-country-methods-catalog-table {
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        .payment-country-methods-page .payment-country-methods-catalog-table thead th {
          background: #eef1f4 !important;
          color: #495057;
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          border-bottom: 1px solid #dee2e6 !important;
          border-top: none !important;
          padding: 0.65rem 1rem;
          vertical-align: middle;
          white-space: nowrap;
        }
        .payment-country-methods-page .payment-country-methods-catalog-table tbody td {
          background: #fff;
          border-top: none !important;
          border-bottom: 1px solid #f1f3f5;
          padding: 0.65rem 1rem;
          vertical-align: middle;
        }
        .payment-country-methods-page .payment-country-methods-catalog-table tbody tr:last-child td {
          border-bottom: none;
        }
        .payment-country-methods-page .payment-country-methods-catalog-table tbody tr:hover td {
          background: #f8fafb;
        }
        .payment-country-methods-page .payment-country-methods-catalog-avatar {
          object-fit: cover;
          border-radius: 50%;
          border: 1px solid #e9ecef;
        }
        .payment-country-methods-page .payment-country-methods-catalog-name {
          font-weight: 600;
          color: #212529;
          font-size: 0.875rem;
          line-height: 1.25;
        }
        .payment-country-methods-page .payment-country-methods-catalog-meta {
          font-size: 0.72rem;
          color: #868e96;
        }
        .payment-country-methods-page .payment-country-methods-catalog-tag {
          display: inline-block;
          font-size: 0.75rem;
          padding: 0.2rem 0.45rem;
          background: #f1f3f5;
          color: #343a40;
          border-radius: 4px;
          border: 1px solid #e9ecef;
        }
        .payment-country-methods-page .payment-country-methods-catalog-pill {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 500;
          color: #495057;
          background: #fff;
          border: 1px solid #dee2e6;
          padding: 0.2rem 0.5rem;
          border-radius: 999px;
        }
        .payment-country-methods-page .payment-country-methods-catalog-status {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 600;
          padding: 0.25rem 0.6rem;
          border-radius: 999px;
        }
        .payment-country-methods-page .payment-country-methods-catalog-status.is-available {
          background: rgba(40, 167, 69, 0.12);
          color: #1e7e34;
        }
        .payment-country-methods-page .payment-country-methods-catalog-status.is-other {
          background: #e9ecef;
          color: #495057;
        }
        .payment-country-methods-page .payment-country-methods-catalog-btn {
          font-size: 0.8rem;
          padding: 0.25rem 0.65rem;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
        }

        .payment-country-methods-page .payment-country-stat-card {
          border-radius: 8px;
          transition: box-shadow 0.2s ease, transform 0.2s ease;
          background: #fff;
        }
        .payment-country-methods-page .payment-country-stat-card:hover {
          box-shadow: 0 0.35rem 1rem rgba(33, 37, 41, 0.08) !important;
        }
        .payment-country-methods-page .payment-country-stat-card--methods {
          border-top: 2px solid #28a745;
        }
        .payment-country-methods-page .payment-country-stat-card--currency {
          border-top: 2px solid #17a2b8;
        }
        .payment-country-methods-page .payment-country-stat-card--rule {
          border-top: 2px solid #6c757d;
        }
        .payment-country-methods-page .payment-country-stat-card__label {
          font-size: 0.65rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #6c757d;
          line-height: 1.25;
          max-width: 72%;
        }
        .payment-country-methods-page .payment-country-stat-card__icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.95rem;
        }
        .payment-country-methods-page .payment-country-stat-card__icon--success {
          background: rgba(40, 167, 69, 0.12);
          color: #28a745;
        }
        .payment-country-methods-page .payment-country-stat-card__icon--info {
          background: rgba(23, 162, 184, 0.12);
          color: #17a2b8;
        }
        .payment-country-methods-page .payment-country-stat-card__icon--neutral {
          background: rgba(108, 117, 125, 0.12);
          color: #495057;
        }
        .payment-country-methods-page .payment-country-stat-card__value {
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.15;
          letter-spacing: -0.02em;
          margin-bottom: 0.2rem;
        }
        .payment-country-methods-page .payment-country-stat-card__value--text {
          font-size: 1.05rem;
          font-weight: 700;
        }
        .payment-country-methods-page .payment-country-stat-card__code {
          display: inline-block;
          margin-top: 0.2rem;
          font-size: 0.7rem;
          font-weight: 600;
          color: #6c757d;
          letter-spacing: 0.03em;
          padding: 0.1rem 0.4rem;
          background: #f1f3f5;
          border-radius: 4px;
        }
        .payment-country-methods-page .payment-country-stat-card__hint {
          font-size: 0.72rem;
          color: #868e96;
          line-height: 1.35;
          margin-top: 0.25rem;
          padding-top: 0;
          padding-bottom: 0;
        }
        .payment-country-methods-page .payment-country-stat-card__rules {
          font-size: 0.78rem;
          line-height: 1.4;
        }
        .payment-country-methods-page .payment-country-stat-card__rule-item {
          margin-bottom: 0.35rem;
        }
        .payment-country-methods-page .payment-country-stat-card__rule-item:last-child {
          margin-bottom: 0;
        }
        .payment-country-methods-page .payment-country-stat-card__bullet {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          margin-top: 0.35rem;
          margin-right: 0.45rem;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
};

export default PaymentCountryMethods;
